import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { F, RA } from '@macs-frontend/core/utils/fp-ts';
import { UUID } from '@macs-frontend/core/utils/uuid';
import {
  ScheduleAssignment,
  ScheduleStartLocation,
} from '@macs-frontend/web-macs/shared';
import { emptyScheduleStartLocation } from '../../initial-objects/emty-start-location';
import { isValidTime, minutesToText, toTime, toTimeWithoutDays } from '../../utils/time';

interface Timeline {
  color: string;
  startTime: string;
  endTime: string;
  name: null | string;
  label: null | string;
  duration: number;
  durationPercent: number;
  address?: string;
  type: string;
  // location: string;
}

@Component({
  selector: 'md-schedule-timeline',
  templateUrl: './schedule-timeline.component.html',
  styleUrls: ['./schedule-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleTimelineComponent implements OnChanges {
  @Input() scheduleStartLocations: ScheduleStartLocation[] = [];
  @Input() scheduleAssignments: ScheduleAssignment[] = [];

  items: readonly Timeline[] = [];
  readonly minDuration = 30;
  readonly minWidth = 90;
  readonly ratio = 0.5;
  readonly grayColor = '#a6b2bf';

  minutesToText = minutesToText;
  toTime = toTime;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scheduleAssignments) {
      this.prepareTimeline();
    }
  }

  prepareTimeline() {
    const items: Timeline[] = [];

    this.scheduleAssignments.forEach(
      (assignment: ScheduleAssignment, i: number, assignments: ScheduleAssignment[]) => {
        const duration = assignment.duration / 60 || 0;
        const startTime = toTimeWithoutDays(assignment.startTime);
        const startLocation: ScheduleStartLocation = assignment.scheduleStartLocations[0]
          ? this.getStartLocation(
              assignment.scheduleStartLocations[0].uuid,
              this.scheduleStartLocations,
            )
          : emptyScheduleStartLocation;
        const timeline = {
          durationPercent: 0,
          color: assignment.color,
          endTime: toTimeWithoutDays(assignment.endTime),
          label: this.getStartLocationsLabel(assignment),
          name: assignment.name,
          startTime,
          duration,
          address: startLocation?.address,
          type: startLocation?.name,
        };

        if (
          i > 0 &&
          isValidTime(toTimeWithoutDays(assignments[i - 1].endTime)) &&
          isValidTime(startTime)
        ) {
          const unusedDuration = assignment.startTime - assignments[i - 1].endTime;
          if (unusedDuration) {
            items.push({
              duration: unusedDuration / 60,
              label: null,
              startTime: toTimeWithoutDays(assignments[i - 1].endTime),
              endTime: toTimeWithoutDays(assignment.startTime),
              name: null,
              color: this.grayColor,
              durationPercent: 0,
              type:
                assignment.scheduleStartLocations[0] &&
                assignment.scheduleStartLocations[0].name,
            });
          }
        }

        items.push(timeline);
      },
    );

    const totalDuration = F.pipe(
      items,
      RA.reduce(0, (acc, { duration }) => acc + duration),
    );

    const minDurationPercent = totalDuration / this.minWidth;

    const minDuration = totalDuration / minDurationPercent;

    const newTotalDuration = F.pipe(
      items,
      RA.reduce(0, (acc, { duration }) => acc + Math.max(duration, minDuration)),
    );

    this.items = F.pipe(
      items,
      RA.map(item => ({
        ...item,
        durationPercent: (Math.max(item.duration, minDuration) / newTotalDuration) * 100,
      })),
    );
  }

  getStartLocationsLabel(
    assignment: ScheduleAssignment,
    startLocations: ScheduleStartLocation[] = this.scheduleStartLocations,
  ) {
    const assignmentStartLocations = assignment.scheduleStartLocations;
    if (assignmentStartLocations.length === startLocations.length) {
      return 'Everyone';
    } else if (assignmentStartLocations.length > 1) {
      return `Multiple (${assignmentStartLocations.length})`;
    }
    const startLocation = assignmentStartLocations[0]
      ? this.getStartLocation(assignmentStartLocations[0].uuid, startLocations)
      : emptyScheduleStartLocation;
    return startLocation ? startLocation.name : '';
  }

  getStartLocation(
    uuid: UUID,
    startLocations: ScheduleStartLocation[],
  ): ScheduleStartLocation {
    // @ts-expect-error
    return startLocations.find(
      (startLocation: ScheduleStartLocation) => startLocation.uuid === uuid,
    );
  }
}
